import React, { useState, useEffect } from 'react';
import { Person } from '../graph/types';
import { getSavedGroups, saveGroup, SavedGroup } from '../utils/groupStorage';

interface SavedGroupsProps {
  currentPeople: Person[];
  onLoadGroup: (people: Person[]) => void;
}

export const SavedGroups: React.FC<SavedGroupsProps> = ({ currentPeople, onLoadGroup }) => {
  const [groups, setGroups] = useState<SavedGroup[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = () => {
    const savedGroups = getSavedGroups();
    setGroups(savedGroups);
  };

  const handleSelectGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupId = e.target.value;
    if (!groupId) return;

    const group = groups.find(g => g.id === groupId);
    if (group) {
      onLoadGroup(group.people);
    }

    // Reset dropdown to placeholder
    e.target.value = '';
  };

  const handleSaveGroup = async () => {
    if (currentPeople.length === 0) {
      setSaveError('Please select at least one person first');
      return;
    }

    if (!newGroupName.trim()) {
      setSaveError('Please enter a group name');
      return;
    }

    try {
      await saveGroup(newGroupName.trim(), currentPeople);
      setNewGroupName('');
      setShowSaveDialog(false);
      setSaveError(null);
      loadGroups();
    } catch (error) {
      setSaveError('Failed to save group');
    }
  };

  const handleCancelSave = () => {
    setShowSaveDialog(false);
    setNewGroupName('');
    setSaveError(null);
  };

  return (
    <div className="saved-groups-container">
      <label className="section-label">Saved Groups</label>

      <div className="saved-groups-controls">
        <select
          className="groups-dropdown"
          onChange={handleSelectGroup}
          disabled={groups.length === 0}
          defaultValue=""
        >
          <option value="" disabled>
            {groups.length === 0 ? 'No saved groups' : 'Load a group...'}
          </option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.people.length})
            </option>
          ))}
        </select>

        <button
          className="save-group-btn"
          onClick={() => setShowSaveDialog(true)}
          disabled={currentPeople.length === 0}
          title={currentPeople.length === 0 ? 'Select people first' : 'Save current attendees as a group'}
        >
          + Save
        </button>
      </div>

      {showSaveDialog && (
        <div className="save-dialog">
          <input
            type="text"
            className="group-name-input"
            placeholder="Enter group name..."
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveGroup()}
            autoFocus
          />
          <div className="dialog-buttons">
            <button className="dialog-btn primary" onClick={handleSaveGroup}>
              Save
            </button>
            <button className="dialog-btn secondary" onClick={handleCancelSave}>
              Cancel
            </button>
          </div>
          {saveError && <div className="save-error">{saveError}</div>}
        </div>
      )}
    </div>
  );
};
